<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { VSwitch } from 'vuetify/components/VSwitch';
import { VTextField } from 'vuetify/components/VTextField';
import { VTextarea } from 'vuetify/components/VTextarea';
import { VFileInput } from 'vuetify/components/VFileInput';
import { VTimePicker } from 'vuetify/components/VTimePicker';
import { VCheckbox } from 'vuetify/components/VCheckbox';
import OperationType from '@/components/filters/OperationType.vue';
import { VAutocomplete } from 'vuetify/components/VAutocomplete';

import AgencyFilter from '@/components/filters/AgencyFilter.vue';
import PackTypeFilter from '@/components/filters/PackType.vue';
import ShippingType from '@/components/filters/ShippingType.vue';

import RepackingType from '@/components/filters/RepackingType.vue';
import CustomerFilter from '@/components/filters/CustomerFilter.vue';
import CustomerAddressFilter from '@/components/filters/CustomerAddressFilter.vue';
import AgentFilter from '@/components/filters/AgentFilter.vue';
import PoBoxFilter from '@/components/filters/PoBoxFilter.vue';
import StatusFilter from '@/components/filters/StatusFilter.vue';
import ShipmentStatusFilter from '@/components/filters/ShipmentStatusFilter.vue';
import CarrierFilter from '@/components/filters/CarrierFilter.vue';
import PackType from '@/components/filters/PackType.vue';
import ConsolType from '@/components/filters/ConsolType.vue';
import LocationFilter from '@/components/filters/LocationFilter.vue';
import StatusType from '@/components/filters/StatusTypeFilter.vue';
import City from '@/components/filters/CityFilter.vue';
import CountryFilter from '@/components/filters/CountryFilter.vue';
import MultiCountryFilter from '@/components/filters/MultiCountryFilter.vue';
import StatePlaceFilter from '@/components/filters/StatePlaceFilter.vue';
import TooltipInputError from '@/components/widgets/TooltipInputError.vue';
import SelectRoles from '@/components/widgets/SelectRoles.vue';
import LabelFilter from '@/components/filters/LabelFilter.vue';
import ProfileFilter from '@/components/filters/ProfileFilter.vue';
import UserTypeFilter from '@/components/filters/UserTypeFilter.vue';
import PasswordField from '@/components/widgets/PasswordField.vue';
import ZoneType from '@/components/filters/ZoneType.vue';
import AccountFilter from '../filters/AccountFilter.vue';
import LocationType from '@/components/filters/LocationType.vue';
import ZoneFilter from '@/components/filters/ZoneFilter.vue';
import LabelsBodyEmail from '@/components/widgets/LabelsBodyEmail.vue';
import TargetLabelFilter from '@/components/filters/TargetLabelFilter.vue';
import WHRateTypeFilter from '@/components/filters/WHRateTypeFilter.vue';
import RegionFilter from '@/components/filters/RegionFilter.vue';
import RoundFilter from '@/components/filters/RoundFilter.vue';
import DesignationFilter from '@/components/filters/DesignationFilter.vue';
import DimensionalFactorFilter from '@/components/filters/DimensionalFactorFilter.vue';
import VolumeFactorFilter from '@/components/filters/VolumeFactorFilter.vue';
import LogisticsTypeFilter from '@/components/filters/LogisticsTypeFilter.vue';
import ArrivedFilter from '@/components/filters/ArrivedFilter.vue';
import CurrencyFilter from '@/components/filters/CurrencyFilter.vue';
import WeightFilter from '@/components/filters/WeightFilter.vue';
import VolumeFilter from '@/components/filters/VolumeFilter.vue';
import RepackagingNoteFilter from '@/components/filters/RepackagingNoteFilter.vue';
import DefaultPlaceFilter from '@/components/filters/DefaultPlaceFilter.vue';
import BasicPlaceInfoFilter from '@/components/filters/BasicPlaceInfoFilter.vue';
import MinDateFilter from '@/components/filters/MinDateFilter.vue';
import MeasureFilter from '@/components/filters/MeasureFilter.vue';
import MeasureServiceFilter from '@/components/filters/MeasureServiceFilter.vue';
import SearchByFilter from '@/components/filters/SearchByFilter.vue';
import ChargeValueFilter from '@/components/filters/ChargeValueFilter.vue';
import LanguageFilter from '@/components/filters/LanguageFilter.vue';
import WhatsappContentFilter from '@/components/filters/WhatsappContentFilter.vue';
import TwilioStatusFilter from '@/components/filters/TwilioStatus.vue';
import CorrelativeType from '@/components/filters/CorrelativeType.vue';
import EmailSecureFilter from '../filters/EmailSecureFilter.vue';
import AssociatedFilter from '../filters/AssociatedFilter.vue';
import ShipmentRateFilter from '@/components/filters/ShipmentRateFilter.vue';
import WarehouseRateFilter from '../filters/WarehouseRateFilter.vue';
import CodeMirrorEditor from './forms/CodeMirrorEditorVue3.vue';
import RoundTypeFilter from '@/components/filters/RoundTypeFilter.vue';
import ShipmentRateTypeFilter from '@/components/filters/ShipmentRateTypeFilter.vue';
import InputTimer from '@/components/filters/InputTimer.vue';

import { defineAsyncComponent } from 'vue';

// Componentes pesados con lazy loading
const QuillEditor = defineAsyncComponent(
    () => import('./forms/QuillEditor.vue')
);
const NumberField = defineAsyncComponent(() => import('./NumberField.vue'));
const ImageField = defineAsyncComponent(() => import('./ImageField.vue'));
const TextDatepicker = defineAsyncComponent(
    () => import('./TextDatepicker.vue')
);

import GroupTariffFilter from '../filters/GroupTariffFilter.vue';
import WeightPayFilter from '@/components/filters/WeightPayFilter.vue';
import RateFilter from '../filters/RateFilter.vue';
import TaxFilter from '../filters/TaxFilter.vue';
import ConsolidatedTypeFilter from '../filters/ConsolidatedTypeFilter.vue';
import WeightPayWarehouseFilter from '../filters/WeightPayWarehouseFilter.vue';
import ServiceFilter from '../filters/ServiceFilter.vue';
import PaidTypeFilter from '../filters/PaidTypeFilter.vue';
import FormattedNumberField from '@/components/widgets/FormattedNumberField.vue';
import FormattedPercentageField from './FormattedPercentageField.vue';
import BranchFilter from '../filters/BranchFilter.vue';
import LocalTransporterFilter from '../filters/LocalTransporterFilter.vue';
import UserStatusFilter from '../filters/UserStatusFilter.vue';
import EmailTagField from '../filters/EmailTagField.vue';
import RateFilterProxy from '../filters/RateFilterProxy.vue';
import PaymentStatusFilter from '../filters/PaymentStatusFilter.vue';
import PlaceFilter from '../filters/PlaceFilter.vue';

const emit = defineEmits(['update:modelValue', 'update:focused', 'keyup']);

const props = defineProps({
    fieldModels: {
        type: Object,
        required: false
    },
    component: {},
    errorMessages: {},
    mode: {
        type: String,
        default: 'edit'
    },
    align: {},
    multiple: {},
    disabled: {},
    rows: {},
    returnObject: {},
    modelValue: {},
    editable: {
        type: Boolean,
        default: true
    },
    label: {},
    documentType: {},
    args: {
        type: Object,
        default: () => ({})
    }
});

const model = computed({
    get: () => props.modelValue,
    set: (value) => {
        emit('update:modelValue', value);
    }
});

const textareaRef = ref(null);

// Calcula el número de filas basado en el contenido
const computedRows = computed(() => {
    if (props.component !== 'VTextarea' || !model.value) {
        return props.rows || 1;
    }

    const text = String(model.value);
    const lineBreaks = (text.match(/\n/g) || []).length;
    const minRows = props.rows || 1;

    // Retorna al menos minRows, o el número de líneas + 1
    return Math.max(minRows, lineBreaks + 1);
});

// Observa cambios en el modelo para ajustar altura
watch(model, () => {
    if (props.component === 'VTextarea') {
        nextTick(() => {
            // Forzar recálculo de altura si es necesario
        });
    }
});

const computedClearable = computed(() => {
    if (
        props.args &&
        Object.prototype.hasOwnProperty.call(props.args, 'clearable')
    ) {
        return props.args.clearable;
    }

    if (
        ['VTextField', 'VTextarea', 'NumberField', 'QuillEditor'].includes(
            props.component
        )
    ) {
        return undefined;
    }

    return props.mode === 'edit';
});

const components = {
    VSwitch,
    VTextField,
    VTextarea,
    VAutocomplete,
    VCheckbox,
    VTimePicker,
    InputTimer,
    VFileInput,
    QuillEditor,
    CodeMirrorEditor,
    TextDatepicker,
    FormattedNumberField,
    FormattedPercentageField,
    ArrivedFilter,
    AgencyFilter,
    ShippingType,
    RepackingType,
    CustomerFilter,
    CustomerAddressFilter,
    AgentFilter,
    PoBoxFilter,
    StatusFilter,
    ShipmentStatusFilter,
    CarrierFilter,
    PackType,
    ConsolType,
    LocationFilter,
    StatusType,
    City,
    NumberField,
    ImageField,
    SelectRoles,
    CountryFilter,
    MultiCountryFilter,
    StatePlaceFilter,
    PackTypeFilter,
    LabelFilter,
    ProfileFilter,
    UserTypeFilter,
    PasswordField,
    ZoneType,
    AccountFilter,
    LocationType,
    ZoneFilter,
    LabelsBodyEmail,
    TargetLabelFilter,
    WHRateTypeFilter,
    RegionFilter,
    RoundFilter,
    DesignationFilter,
    DimensionalFactorFilter,
    VolumeFactorFilter,
    LogisticsTypeFilter,
    CurrencyFilter,
    WeightFilter,
    VolumeFilter,
    RepackagingNoteFilter,
    DefaultPlaceFilter,
    BasicPlaceInfoFilter,
    MinDateFilter,
    MeasureFilter,
    SearchByFilter,
    ChargeValueFilter,
    EmailSecureFilter,
    AssociatedFilter,
    GroupTariffFilter,
    WeightPayFilter,
    RateFilter,
    TaxFilter,
    ConsolidatedTypeFilter,
    WeightPayWarehouseFilter,
    ServiceFilter,
    PaidTypeFilter,
    MeasureServiceFilter,
    BranchFilter,
    LocalTransporterFilter,
    UserStatusFilter,
    EmailTagField,
    RateFilterProxy,
    PaymentStatusFilter,
    WarehouseRateFilter,
    ShipmentRateFilter,
    PlaceFilter,
    WhatsappContentFilter,
    TwilioStatusFilter,
    LanguageFilter,
    RoundTypeFilter,
    ShipmentRateTypeFilter,
    OperationType,
    CorrelativeType
};
</script>

<template>
    <component
        ref="textareaRef"
        :field-models="fieldModels"
        density="compact"
        hide-details
        :hide-label="!label"
        :label="label"
        :rows="computedRows"
        :is="components[component]"
        v-model="model"
        :error-messages="errorMessages"
        :variant="
            editable ? (mode === 'edit' ? 'outlined' : 'filled') : 'filled'
        "
        :readonly="editable ? (mode === 'edit' ? false : true) : true"
        color="primary"
        :class="[
            align ? `text-${align}` : '',
            { 'no-focus': !editable || mode === 'view' }
        ]"
        :return-object="returnObject"
        @update:focused="(state) => $emit('update:focused', state)"
        @update:args="(args) => $emit('update:args', args)"
        @keyup="(event) => $emit('keyup', event)"
        v-bind="args"
        :clearable="computedClearable"
        :multiple="multiple"
        :disabled="disabled"
        :document-type="documentType"
        :tabindex="!editable || mode === 'view' ? -1 : undefined"
        :args="args"
    >
        <template v-if="errorMessages" v-slot:prepend>
            <TooltipInputError :error="errorMessages"></TooltipInputError>
        </template>
    </component>
</template>

<style lang="scss" scoped>
.text-right :deep(.v-field__input),
.text-right :deep(.ql-editor) {
    text-align: right;
}
.text-left :deep(.v-field__input),
.text-left :deep(.ql-editor) {
    text-align: left;
}
.text-center :deep(.v-field__input),
.text-center :deep(.ql-editor) {
    text-align: center;
}

.no-focus {
    pointer-events: none;
}

.no-focus :deep(.v-field__input),
.no-focus :deep(.ql-editor) {
    cursor: default !important;
}

/* Make VTextarea adjust to content */
:deep(.v-textarea) {
    .v-field__input {
        padding-top: 8px;
        padding-bottom: 8px;
        overflow-y: hidden; /* Oculta scroll mientras se ajusta */
    }
}

/* When in compact density */
:deep(.v-input--density-compact .v-textarea .v-field__input) {
    padding-top: 4px;
    padding-bottom: 4px;
}
</style>
